
import React from "react";
import { CellPosition, ColumnKey, Exercise } from "../../types/workout-types";
import { WorkoutCell } from "../WorkoutCell";

interface ExerciseRowProps {
  exercise: Exercise;
  rowIndex: number;
  columns: ColumnKey[];
  activeCell: CellPosition | null;
  editMode: boolean;
  tempValue: string;
  currentDayIndex: number;
  dayIndex?: number;
  handleCellClick: (rowIndex: number, colIndex: number, value: string) => void;
  startEditing: (rowIndex: number, colIndex: number, value: string) => void;
  setActiveCell: (cell: CellPosition | null) => void;
  setHasEdited: (hasEdited: boolean) => void;
  finishEditingWithSave: (id: string, field: ColumnKey, tempValue: string) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => void;
  inputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  setTempValue: (value: string) => void;
}

export function ExerciseRow({
  exercise,
  rowIndex,
  columns,
  activeCell,
  editMode,
  tempValue,
  currentDayIndex,
  dayIndex,
  handleCellClick,
  startEditing,
  setActiveCell,
  setHasEdited,
  finishEditingWithSave,
  handleKeyDown,
  inputRefs,
  setTempValue,
}: ExerciseRowProps) {
  const isRowActive = activeCell?.rowIndex === rowIndex && 
                      currentDayIndex === (dayIndex || currentDayIndex);

  return (
    <tr
      key={`${exercise.id}`}
      className={`transition-colors duration-300 ${
        isRowActive ? "bg-white/10" : "hover:bg-white/5"
      }`}
    >
      {columns.map((column, colIndex) => {
        const isActive = isRowActive && activeCell?.colIndex === colIndex;
        const cellId = `cell-${rowIndex}-${colIndex}`;
        
        // Get the correct cell value based on column and available data
        let cellValue = "";
        
        // Handle the different property names between API and app models
        if (column === "exercise") {
          cellValue = exercise.exercise || exercise.name || "";
        } else if (column === "rpe") {
          cellValue = exercise.rpe || exercise.intensity || "";
        } else if (column === "rest") {
          cellValue = exercise.rest || (exercise.rest_seconds ? exercise.rest_seconds.toString() : "");
        } else if (column === "notes") {
          // Display exercise_notes if available, fallback to notes, or show "No notes available"
          cellValue = exercise.exercise_notes || exercise.notes || "No notes available";
        } else {
          cellValue = exercise[column] || "";
        }

        return (
          <WorkoutCell
            key={`${exercise.id}-${column}`}
            isActive={isActive}
            editMode={editMode}
            cellValue={cellValue}
            tempValue={tempValue}
            rowIndex={rowIndex}
            colIndex={colIndex}
            cellId={cellId}
            column={column}
            onEdit={(value) => {
              if (isActive && editMode) {
                setHasEdited(true);
                setTempValue(value);
              }
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (isActive && editMode) {
                finishEditingWithSave(exercise.id, column, tempValue);
              }
            }}
            onFocus={() => {
              if (
                !activeCell ||
                activeCell.rowIndex !== rowIndex ||
                activeCell.colIndex !== colIndex
              ) {
                setActiveCell({ rowIndex, colIndex });
              }
            }}
            onClick={() => handleCellClick(rowIndex, colIndex, cellValue)}
            onDoubleClick={() => {
              startEditing(rowIndex, colIndex, cellValue);
            }}
            onClickOutside={() => {
              if (editMode && isActive) {
                finishEditingWithSave(exercise.id, column, tempValue);
              }
              setActiveCell(null);
              setHasEdited(false);
            }}
            setRef={(el) => (inputRefs.current[cellId] = el)}
          />
        );
      })}
    </tr>
  );
}
